import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { FileDown, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { PageHeader } from '../../components/common/PageHeader';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { CourseCard } from '../../components/learning/CourseCard';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Course, CourseCategoryRef, CourseStatus } from '../../types/learning.types';
import { learningApi, learningExportUrl } from '../../utils/learning-api';

const ALL = 'all';
const STATUS_OPTIONS: CourseStatus[] = ['Draft', 'Active', 'Archived'];

export default function CourseLibraryPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.COURSE);
    const navigate = useNavigate();

    const [courses, setCourses] = useState<Course[]>([]);
    const [categories, setCategories] = useState<CourseCategoryRef[]>([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [categoryId, setCategoryId] = useState<string>(ALL);
    const [status, setStatus] = useState<string>(ALL);

    // Category picker options: loaded once, independent of the filtered list.
    useEffect(() => {
        if (!canRead) return;
        learningApi.courses
            .options()
            .then((options) => setCategories(options.categories))
            .catch(() => {
                // The list request below surfaces real failures; an empty picker is harmless.
            });
    }, [canRead]);

    // Debounce only the search box; the two selects apply immediately.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const load = useCallback(async () => {
        try {
            setCourses(
                await learningApi.courses.list({
                    search: debouncedSearch || undefined,
                    categoryId: categoryId === ALL ? undefined : Number(categoryId),
                    status: status === ALL ? undefined : status,
                }),
            );
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to load courses');
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, categoryId, status]);

    useEffect(() => {
        if (canRead) load();
    }, [canRead, load]);

    const handleArchive = async (course: Course) => {
        try {
            await learningApi.courses.archive(course.id);
            toast.success(`${course.title} archived`);
            load();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to archive course');
        }
    };

    const handleDelete = async (course: Course) => {
        try {
            await learningApi.courses.remove(course.id);
            toast.success(`${course.title} deleted`);
            load();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to delete course');
        }
    };

    // The export endpoint is a raw URL (not an api.get), so mirror stock-api's
    // downloadExport: fetch with the Bearer token, then save the blob.
    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                learningExportUrl({
                    search: debouncedSearch || undefined,
                    categoryId: categoryId === ALL ? undefined : Number(categoryId),
                    status: status === ALL ? undefined : status,
                    format: 'csv',
                }),
                { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            );
            if (!response.ok) throw new Error('Failed to export courses');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `courses_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(anchor);
            anchor.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(anchor);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to export courses');
        }
    };

    if (!canRead) return <AccessRestricted />;

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            <PageHeader
                title="Course Library"
                description="Build, publish and retire the courses available to Proview staff."
                actions={
                    <>
                        <Button variant="outline" onClick={handleExport}>
                            <FileDown size={16} />
                            Export CSV
                        </Button>
                        <Can I={ACTIONS.CREATE} a={RESOURCES.COURSE}>
                            <Button asChild>
                                <Link to="/learning/courses/new">
                                    <Plus size={16} />
                                    Add Course
                                </Link>
                            </Button>
                        </Can>
                    </>
                }
            />

            {/* Filters — applied server-side on change */}
            <div className="flex flex-col md:flex-row md:items-center w-full gap-4">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search courses..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <div className="w-full md:w-[200px] min-w-0">
                    <Select value={categoryId} onValueChange={setCategoryId}>
                        <SelectTrigger className="w-full bg-white">
                            <SelectValue placeholder="All categories" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>All categories</SelectItem>
                            {categories.map((category) => (
                                <SelectItem key={category.id} value={String(category.id)}>
                                    {category.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-full md:w-[160px] min-w-0">
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="w-full bg-white">
                            <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>All statuses</SelectItem>
                            {STATUS_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>
                                    {option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {courses.length} courses
                </span>
            </div>

            {loading && courses.length === 0 ? (
                <LoadingPageSkeleton />
            ) : courses.length === 0 ? (
                <div className="text-center text-muted-foreground py-16">
                    No courses match these filters.
                </div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {courses.map((course) => (
                        <CourseCard
                            key={course.id}
                            course={course}
                            onAssign={(c) => navigate(`/learning/register/${c.id}`)}
                            onArchive={handleArchive}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
